import { useCallback, useMemo } from 'react';
import {
  ColorValues,
  Divider,
  Flex,
  IconEmptyInform,
  Typography,
} from '@banhala/ui-kit';
import { Tooltip } from 'shared/src/component/Tooltip';
import { useAtom } from 'jotai';
import { useRankingFilters } from '../queries/useRankingFilters';
import { rankingFilterAtom } from '../states/rankingFilter.atom';
import { MarketTypeFilter } from './MarketTypeFilter';
import { CategoryFilter } from './CategoryFilter';
import { SubCategoryFilter } from './SubCategoryFilter';
import { AgeFilter } from './AgeFilter';
import type {
  RankingFilterCategory,
  RankingFilterMarketVerticalType,
} from 'shared/src/api';
import { isIOS, isNotNil } from 'shared/src/utils';
import { useAgeSelectBottomSheet } from './AgeSelectBottomSheet';
import { useLogClickTypeFilter } from '../hooks/useLogClickTypeFilter';
import { useLogClickFilter } from '../hooks/useLogClickFilter';
import { useLogApplyFilter } from '../hooks/useLogApplyFilter';
import { useBottomSheetRoute, useIsInFragmentScreens } from 'shared/src/hooks';
import type { RankingFilterAge } from 'shared/src/api';

interface RankingGoodsListHeaderProps {
  lastUpdatedBeforeMessage?: string;
}

export const RankingGoodsListHeader = ({
  lastUpdatedBeforeMessage,
}: RankingGoodsListHeaderProps) => {
  const { data: rankingFilters } = useRankingFilters();
  const [filter, setFilter] = useAtom(rankingFilterAtom);
  const { show: showAgeSelectBottomSheet } = useAgeSelectBottomSheet();
  const { open: openBottomSheetRoute } = useBottomSheetRoute();
  const isInFragmentScreens = useIsInFragmentScreens();

  const logClickTypeFilter = useLogClickTypeFilter();
  const logClickFilter = useLogClickFilter();
  const logApplyFilter = useLogApplyFilter();

  const selectedMarketType =
    filter.selectedMarketType ??
    rankingFilters.market_vertical_types[0] ??
    null;

  const categories = selectedMarketType?.categories ?? [];

  const selectedCategory = filter.selectedCategory ?? categories[0] ?? null;

  const subCategories = useMemo(() => {
    if (!selectedCategory?.categories) return [];
    return selectedCategory.categories;
  }, [selectedCategory]);

  const selectedSubCategory =
    filter.selectedSubCategory ?? subCategories[0] ?? null;

  const selectedPeriod = filter.selectedPeriod ?? null;

  const handleSelectMarketType = useCallback(
    (marketType: RankingFilterMarketVerticalType) => {
      if (marketType.sno === selectedMarketType?.sno) return;
      logClickTypeFilter(marketType.name);
      setFilter(prev => ({
        ...prev,
        selectedMarketType: marketType,
        selectedCategory: null,
        selectedSubCategory: null,
      }));
    },
    [logClickTypeFilter, selectedMarketType?.sno, setFilter],
  );

  const handleSelectCategory = useCallback(
    (category: RankingFilterCategory) => {
      setFilter(prev => {
        const next = {
          ...prev,
          selectedCategory: category,
          selectedSubCategory: null,
        };
        logApplyFilter(next, 'CATEGORY');
        return next;
      });
    },
    [logApplyFilter, setFilter],
  );

  const handleSelectSubCategory = useCallback(
    (category: RankingFilterCategory) => {
      setFilter(prev => {
        const next = {
          ...prev,
          selectedSubCategory: category,
        };
        logApplyFilter(next, 'CATEGORY');
        return next;
      });
    },
    [logApplyFilter, setFilter],
  );

  const handlePressAge = useCallback(() => {
    logClickFilter('AGE');
    const params = {
      list: rankingFilters.ages,
      selected: filter.selectedAge?.query_value['age_tags[]'] ?? null,
    };
    const promise = isInFragmentScreens
      ? openBottomSheetRoute<typeof params, { nextValue: RankingFilterAge }>(
          'goods-ranking-age',
          params,
        )
      : showAgeSelectBottomSheet(params);
    promise.then(result => {
      if (result.type === 'confirm') {
        setFilter(prev => {
          const next = {
            ...prev,
            selectedAge: result.data.nextValue,
          };
          logApplyFilter(next, 'AGE');
          return next;
        });
      }
    });
  }, [
    isInFragmentScreens,
    logApplyFilter,
    logClickFilter,
    openBottomSheetRoute,
    showAgeSelectBottomSheet,
    rankingFilters.ages,
    filter.selectedAge?.query_value,
    setFilter,
  ]);

  return (
    <Flex>
      <MarketTypeFilter
        marketTypes={rankingFilters.market_vertical_types}
        selectedSno={selectedMarketType?.sno ?? null}
        onSelect={handleSelectMarketType}
      />
      <Divider size="small" />
      <CategoryFilter
        categories={categories}
        selectedSno={selectedCategory?.sno ?? null}
        onSelect={handleSelectCategory}
      />
      <SubCategoryFilter
        subCategories={subCategories}
        selectedSno={selectedSubCategory?.sno ?? null}
        onSelect={handleSelectSubCategory}
      />
      {(isNotNil(selectedCategory?.sno) ||
        isNotNil(selectedSubCategory?.sno)) && <Divider size="small" />}
      <Flex.Row
        alignItems="center"
        justifyContent="space-between"
        padding={{ vertical: 16, left: 16, right: 0 }}
      >
        <Flex.Row alignItems="center" gap={2}>
          {lastUpdatedBeforeMessage && (
            <Typography type="body3" color="content_tertiary">
              {lastUpdatedBeforeMessage}
            </Typography>
          )}
          <Tooltip
            label={
              selectedPeriod?.tooltip ??
              '오늘 구매와 클릭수 기반 인기 상품을 확인할 수 있습니다.'
            }
            topAdjustment={isIOS ? 200 : 132}
            contentStyle={{
              paddingHorizontal: 14,
              paddingVertical: 12,
              borderWidth: 1,
              backgroundColor: ColorValues.slate60,
            }}
          >
            <IconEmptyInform size={16} />
          </Tooltip>
        </Flex.Row>
        <AgeFilter
          selectedAge={filter.selectedAge ?? null}
          onPress={handlePressAge}
        />
      </Flex.Row>
      <Divider size="small" />
    </Flex>
  );
};
